import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import usersService from '../../services/usersService';

const initialState = {
  users: [],
  totalUsers: 0,
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
  error: null,
  success: false,
};

export const fetchUsers = createAsyncThunk(
  'users/fetchAll',
  async (params = {}, thunkAPI) => {
    try {
      return await usersService.getAll(params);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch users';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'users/updateStatus',
  async ({ id, isActive }, thunkAPI) => {
    try {
      return await usersService.updateStatus(id, isActive);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update user status';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const updateUserRole = createAsyncThunk(
  'users/updateRole',
  async ({ id, role }, thunkAPI) => {
    try {
      return await usersService.updateRole(id, role);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update user role';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const verifyUser = createAsyncThunk(
  'users/verify',
  async ({ id, isVerified, note }, thunkAPI) => {
    try {
      return await usersService.verify(id, isVerified, note);
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update verification';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

export const deleteUser = createAsyncThunk(
  'users/delete',
  async (id, thunkAPI) => {
    try {
      await usersService.delete(id);
      return id;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to delete user';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetSuccess: (state) => {
      state.success = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch users
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = action.payload.users || [];
        state.totalUsers = action.payload.total || 0;
        state.currentPage = action.payload.page || 1;
        state.totalPages = action.payload.totalPages || 1;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update status
      .addCase(updateUserStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex((u) => u._id === action.payload.user._id);
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        state.success = true;
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Update role
      .addCase(updateUserRole.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateUserRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex((u) => u._id === action.payload.user._id);
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        state.success = true;
      })
      .addCase(updateUserRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Verify user
      .addCase(verifyUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(verifyUser.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.users.findIndex((u) => u._id === action.payload.user._id);
        if (index !== -1) {
          state.users[index] = action.payload.user;
        }
        state.success = true;
      })
      .addCase(verifyUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Delete user
      .addCase(deleteUser.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.users = state.users.filter((u) => u._id !== action.payload);
        state.totalUsers -= 1;
        state.success = true;
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, resetSuccess } = usersSlice.actions;
export default usersSlice.reducer;
